import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Loader } from './consent';

interface IframePortalProps extends React.IframeHTMLAttributes<HTMLIFrameElement> {
    children?: any;
    iframeClassName?: string;
    htmlContent?: string; // Add a new prop for raw HTML content
}

const IframePortal: React.FC<IframePortalProps> = ({ children, iframeClassName, htmlContent, ...props }) => {
    const [iframeBody, setIframeBody] = useState<HTMLElement | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [iframeHeight, setIframeHeight] = useState(0);

    const iframeRef = useRef<HTMLIFrameElement>(null);

    const handleLoad = () => {
        if (iframeRef.current) {
            iframeRef.current.contentWindow.postMessage('addClass', '*');

            setIsLoaded(true);
        }
    };

    useEffect(() => {
        const iframeDocument = iframeRef.current?.contentDocument;
        if (iframeDocument) {
            setIframeBody(iframeDocument.body);
        }
    }, []);

    useEffect(() => {
        if (iframeBody && htmlContent) {
            iframeBody.innerHTML = htmlContent; // Insert raw HTML content
        }
    }, [iframeBody, htmlContent]);

    // Listen for messages from the iframe (to adjust height, etc.)
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data.frameHeight) {
                setIframeHeight(event.data.frameHeight); // Adjust iframe height dynamically
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    return (
        <>
            {!isLoaded && <Loader />}
            <iframe
                ref={iframeRef}
                className={iframeClassName}
                {...props}
                title="Portal Iframe"
                onLoad={handleLoad}
                style={{ opacity: isLoaded ? '1' : '0' }}
            >
                {iframeBody && !htmlContent && (createPortal(children, iframeBody) as ReactNode)}
            </iframe>
        </>
    );
};

/**
 * IframePortal Component
 *
 * This component allows rendering content inside an iframe either by:
 * 1. **Embedding External Content (Link)**:
 *    - You can provide an external URL via the `src` prop to embed content from another site.
 *    - Example:
 *    ```tsx
 *    <IframePortal src="https://example.com" iframeClassName="custom-iframe" />
 *    ```
 * 2. **Embedding JSX Content (React Components or HTML)**:
 *    - You can pass JSX elements (such as React components or HTML) as children to render within the iframe.
 *    - The content passed as `children` will be rendered inside the iframe using React's Portal API.
 *    - Example:
 *    ```tsx
 *    <IframePortal iframeClassName="custom-iframe">
 *      <h1>This is rendered inside an iFrame!</h1>
 *      <p>Some additional content can be placed here.</p>
 *    </IframePortal>
 *    ```
 * 3. **Rendering Raw HTML**:
 *    - You can pass raw HTML content using the `htmlContent` prop, which will be inserted directly into the iframe's body.
 *    - Example:
 *    ```tsx
 *    <IframePortal htmlContent="<h1>This is raw HTML inside an iframe!</h1>" />
 *    ```
 *
 * Props:
 * - `src`: (optional) The URL to be embedded as an external link inside the iframe.
 * - `iframeClassName`: (optional) Class name to apply CSS styles to the iframe.
 * - `children`: (optional) JSX elements (React components or HTML) that will be rendered inside the iframe.
 * - `htmlContent`: (optional) A string containing raw HTML to be rendered inside the iframe.
 *
 * Usage Notes:
 * - If `src` is provided, the iframe will load the external content.
 * - If `htmlContent` is provided, it will render raw HTML inside the iframe.
 * - If `children` are provided and `htmlContent` is not, the children will be rendered using React Portals.
 */

export default IframePortal;
